import React, { useState, useRef, useEffect } from "react";
import {Mode, StepDefinition} from "../models/steps";
import {Turn} from "../models/assessment";


export interface DimensionChatProps {
    step: StepDefinition;
    mode: Mode;
    history: Turn[];
    onAppendTurn: (turn: Turn) => void;
}


export function DimensionChat(props: DimensionChatProps) {
    const { step, history, onAppendTurn } = props;

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [input, setInput] = useState("");
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInput(e.target.value);

        const el = textareaRef.current;
        if (!el) return;

        el.style.height = "auto";
        el.style.height = Math.min(el.scrollHeight, 160) + "px";
    };

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [history]);

    useEffect(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = Math.min(el.scrollHeight, 160) + "px";
    }, [sidebarOpen]);

    const handleSend = () => {
        const trimmed = input.trim();
        if (!trimmed) return;

        // TODO: replace with real backend call
        const mockBotResponse = "This is where the bot’s reply will go.";

        onAppendTurn({
            user_prompt: trimmed,
            bot_response: mockBotResponse,
        });

        setInput("");
    };

    return (
        <div className="flex w-full h-full bg-bg text-text rounded-xl overflow-hidden border border-border">
            {/* MAIN COLUMN */}
            <div className="flex flex-col flex-1">
                {/* Header */}
                <header className="flex items-center justify-between bg-header text-white px-6 py-3 shadow-sm">
                    <div>
                        <h2 className="text-base font-semibold">{step.label}</h2>
                        {step?.name && (
                            <div className="text-xs text-bg/70">{step.name}</div>
                        )}
                    </div>

                    <button
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        className="text-xs rounded bg-accent hover:bg-accentHover text-white px-3 py-1 shadow"
                    >
                        {sidebarOpen ? "Hide details" : "Show details"}
                    </button>
                </header>

                {/* Chat history */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {history.length === 0 && (
                        <p className="text-gray-400 text-sm">Start the conversation…</p>
                    )}

                    {history.map((turn, index) => (
                        <div key={index} className="space-y-3">
                            <div className="flex justify-end">
                                <div className="inline-block max-w-[70%] rounded-2xl px-4 py-2 bg-blue-600 text-white shadow">
                                    {turn.user_prompt}
                                </div>
                            </div>

                            <div className="flex justify-start">
                                <div className="inline-block max-w-[70%] rounded-2xl px-4 py-2 bg-gray-100 text-gray-900 border border-gray-300 shadow">
                                    {turn.bot_response}
                                </div>
                            </div>
                        </div>
                    ))}

                    <div ref={bottomRef} />
                </div>

                {/* Input bar */}
                <div className="border-t border-border px-4 py-3 bg-bg flex items-end gap-2">
                    <textarea
                        ref={textareaRef}
                        value={input}
                        onChange={handleInputChange}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                handleSend();
                            }
                        }}
                        placeholder="Type your question…"
                        rows={1}
                        className="flex-1 rounded border border-border px-3 py-2 text-sm
                                   focus:outline-none focus:ring-2 focus:ring-accent/60 focus:border-accent
                                   resize-none leading-relaxed max-h-40"
                    />

                    <button
                        onClick={handleSend}
                        className="text-sm rounded bg-accent hover:bg-accentHover text-white px-4 py-2 whitespace-nowrap"
                    >
                        Send
                    </button>
                </div>
            </div>

            {/* Sidebar */}
            {sidebarOpen && (
                <aside className="w-[260px] bg-bg border-l border-border px-4 py-4 overflow-y-auto">
                    <h3 className="text-text font-semibold mb-2 text-xs uppercase tracking-wide">
                        Current rank estimate
                    </h3>
                    <div className="mb-4 text-sm text-text/80">
                        [hidden or shown based on mode]
                    </div>

                    <h3 className="text-text font-semibold mb-2 text-xs uppercase tracking-wide">
                        Fact summary
                    </h3>
                    <ul className="space-y-1 text-sm text-text">
                        <li>• Fact 1</li>
                        <li>• Fact 2</li>
                        <li>• Fact 3</li>
                    </ul>
                </aside>
            )}
        </div>
    );
}